/**
 * @architecture-header 1
 * @layer app
 * @domain parallel-development
 * @responsibility Compose Agent B-owned workspace content states without changing shared routes.
 * @state-owner Agent B feature modules
 * @allowed-imports React types; Agent B module public APIs; shared composition contract
 * @forbidden Agent A modules; routes; direct API clients
 * @public-api WorkspaceSectionContent aggregator
 * @side-effects delegated to rendered feature content
 * @tests WorkspaceSectionContent.test.tsx and workspace Playwright flows
 */

import type { ReactNode } from 'react';
import { ClassroomCollectionState, StudentClassroomLeave } from '../../../modules/classrooms';
import type { WorkspaceSectionContentContext } from '../types';

export function agentBSectionContent(
  context: WorkspaceSectionContentContext,
  fallback: ReactNode,
): ReactNode | undefined {
  if (
    context.role === 'student' &&
    context.section === 'room' &&
    (context.surface.endsWith('main') || context.surface === 'mobile-list')
  ) {
    return (
      <StudentClassroomLeave surface={context.surface.startsWith('desktop') ? 'desktop' : 'mobile'}>
        {fallback}
      </StudentClassroomLeave>
    );
  }
  if (context.role !== 'teacher' || context.section !== 'room') return undefined;
  const surface = context.surface.endsWith('list') ? 'list' : 'main';
  // Keep four independently addressable composition boundaries. The shell can
  // mount the same module on two breakpoints, but a mobile roster must never
  // inherit the Desktop DOM and merely rely on CSS resizing.
  return (
    <section data-agent-b-surface={context.surface} data-surface-kind={surface}>
      <ClassroomCollectionState surface={surface}>{fallback}</ClassroomCollectionState>
    </section>
  );
}
